<template>
  <aside
    class="cookie-consent"
    role="region"
    aria-labelledby="cookie-consent-title"
    :dir="['ar', 'ur'].includes(locale) ? 'rtl' : 'ltr'"
    @keydown.esc.stop="closePreferences"
  >
    <div class="consent-shell">
      <div class="summary">
        <span class="brand" aria-hidden="true">IDMANIFY</span>
        <p id="cookie-consent-title" class="title">
          {{ $t('cookieConsent.title') }}
        </p>
        <p class="description">{{ $t('cookieConsent.description') }}</p>
        <p class="details">
          {{ $t('cookieConsent.withdrawalHint') }}
          <nuxt-link
            class="privacy-link"
            :to="$localePath({ name: 'privacy' })"
            target="_blank"
            rel="noopener"
            >{{ $t('contracts.privacy') }}</nuxt-link
          >
        </p>
        <p class="statistics-note">
          {{ $t('cookieConsent.plausibleSummary') }}
        </p>
      </div>

      <div class="controls">
        <button
          type="button"
          data-cy="accept-cookies"
          class="choice-button"
          @click="acceptAll"
        >
          {{ $t('cookieConsent.acceptAll') }}
        </button>
        <button
          type="button"
          data-cy="reject-cookies"
          class="choice-button"
          @click="decline"
        >
          {{ $t('cookieConsent.necessaryOnly') }}
        </button>
        <button
          ref="customizeButton"
          type="button"
          class="text-button"
          :aria-expanded="showCustomize"
          :aria-controls="showCustomize ? 'cookie-preferences' : undefined"
          @click="showCustomize = !showCustomize"
        >
          {{ $t('actions.customize') }}
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            :class="{ expanded: showCustomize }"
          >
            <path
              d="m4 6 4 4 4-4"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="showCustomize" id="cookie-preferences" class="preferences">
      <div class="necessary-row">
        <strong>{{ $t('cookieOptions.necessary') }}</strong>
        <p>{{ $t('cookieConsent.necessaryDescription') }}</p>
      </div>
      <label class="preference-row">
        <input
          v-model="analyticsAllowed"
          type="checkbox"
          data-cy="consent-analytics"
        />
        <span>
          <strong>{{ $t('cookieConsent.analyticsTitle') }}</strong>
          <small>{{ $t('cookieConsent.analyticsDescription') }}</small>
        </span>
      </label>
      <label class="preference-row">
        <input
          v-model="marketingAllowed"
          data-cy="consent-marketing"
          type="checkbox"
        />
        <span>
          <strong>{{ $t('cookieConsent.marketingTitle') }}</strong>
          <small>{{ $t('cookieConsent.marketingDescription') }}</small>
        </span>
      </label>
      <div class="preference-actions">
        <button type="button" class="choice-button" @click="saveSelection">
          {{ $t('cookieConsent.saveSelection') }}
        </button>
      </div>
      <div class="statistics-settings"><PlausiblePrivacy /></div>
    </div>
  </aside>
</template>

<script setup>
const { locale } = useI18n();
const customizeButton = ref(null);
function closePreferences() {
  if (!showCustomize.value) return;
  showCustomize.value = false;
  customizeButton.value?.focus();
}
const plausiblePrivacy = usePlausiblePrivacy();
function preservePlausibleChoice() {
  plausiblePrivacy.sync();
  // Preserve an earlier version-2 refusal before replacing its consent cookie.
  if (plausiblePrivacy.optedOut.value) plausiblePrivacy.save(true);
}
const emit = defineEmits(['agreed', 'declined', 'saved']);
const props = defineProps({
  initialAnalytics: Boolean,
  initialMarketing: Boolean
});
const analyticsAllowed = ref(props.initialAnalytics);
watch(
  () => props.initialAnalytics,
  (value) => {
    analyticsAllowed.value = value;
  }
);
watch(
  () => props.initialMarketing,
  (value) => {
    marketingAllowed.value = value;
  }
);

const showCustomize = ref(false);
const marketingAllowed = ref(props.initialMarketing);

function acceptAll() {
  preservePlausibleChoice();
  emit('agreed');
}

function decline() {
  preservePlausibleChoice();
  emit('declined');
}

function saveSelection() {
  preservePlausibleChoice();
  emit('saved', {
    analytics: analyticsAllowed.value,
    marketing: marketingAllowed.value
  });
}
</script>

<style scoped>
.cookie-consent {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: 12px;
  right: max(12px, env(safe-area-inset-right));
  left: max(12px, env(safe-area-inset-left));
  bottom: max(12px, env(safe-area-inset-bottom));
  z-index: 12000;
  width: auto;
  max-width: 1040px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px - env(safe-area-inset-bottom));
  margin-inline: auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  border: 1px solid #b9a78b;
  border-top: 3px solid #ff8a00;
  border-radius: 20px;
  --privacy-text: #ddd2c1;
  --privacy-accent: #f1bd6c;
  background: #211d18;
  color: #fffaf2;
  box-shadow:
    0 16px 44px #00000066,
    0 0 0 1px #ffffff33;
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  color-scheme: dark;
}
.consent-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 270px);
  gap: 32px;
  align-items: center;
  padding: 24px 28px;
}
.summary {
  min-width: 0;
}
.brand {
  display: block;
  margin-bottom: 8px;
  color: #c7baa5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
}
.title {
  margin: 0 0 8px;
  font-size: clamp(18px, 2vw, 22px);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.025em;
}
.description,
.details,
.statistics-note {
  margin: 0;
}
.description {
  color: #ddd2c1;
}
.details {
  margin-top: 8px;
  color: #c7baa5;
  font-size: 12px;
}
.statistics-note {
  margin-top: 8px;
  color: #c7baa5;
  font-size: 12px;
}
.privacy-link {
  color: #ddd2c1;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.controls {
  display: grid;
  gap: 10px;
  min-width: 0;
}
button {
  min-height: 44px;
  min-width: 0;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: background-color 140ms ease;
}
.choice-button {
  background: #f1bd6c;
  border-color: #f5d4a4;
  color: #29241f;
}
.choice-button:hover {
  background: #f6c987;
}
.text-button {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #ddd2c1;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.text-button:hover {
  background: #3b3125;
}
.text-button svg {
  flex-shrink: 0;
}
.text-button svg.expanded {
  transform: rotate(180deg);
}
button:focus-visible,
a:focus-visible,
input:focus-visible {
  outline: 2px solid #f1bd6c;
  outline-offset: 3px;
}
.preferences {
  display: grid;
  gap: 16px;
  padding: 20px 28px 24px;
  border-top: 1px solid #6d5e49;
  background: #2b251e;
}
.necessary-row {
  padding-inline-start: 32px;
}
.necessary-row strong,
.preference-row strong {
  display: block;
  font-size: 14px;
}
.necessary-row p,
.preference-row small {
  display: block;
  margin: 3px 0 0;
  color: #c7baa5;
  font-size: 13px;
}
.preference-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding-block: 6px;
  cursor: pointer;
}
.preference-row input {
  width: 20px;
  height: 20px;
  margin-top: 3px;
  accent-color: #f1bd6c;
}
.preference-actions {
  display: flex;
  justify-content: flex-end;
}
.statistics-settings {
  padding-top: 16px;
  border-top: 1px solid #6d5e49;
}
@media (max-width: 767px) {
  .cookie-consent {
    border-radius: 16px;
  }
  .consent-shell {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 18px;
  }
  .controls {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .text-button {
    grid-column: 1 / -1;
  }
  .preferences {
    padding: 18px;
  }
  .preference-actions {
    display: grid;
  }
}
@media (max-width: 359px) {
  .controls {
    grid-template-columns: 1fr;
  }
  .consent-shell,
  .preferences {
    padding: 16px;
  }
}
@media (prefers-reduced-motion: reduce) {
  button {
    transition: none;
  }
}
@media (forced-colors: active) {
  .cookie-consent {
    border-color: CanvasText;
  }
  .choice-button {
    border-color: ButtonText;
  }
}
</style>
